import { Firestore, doc, setDoc } from '@angular/fire/firestore';
import { Database, ref as dbRef, get } from '@angular/fire/database';
import { captureException } from '@sentry/angular';
import { Progress } from './migration-types';
import { ServiceUpdateModel } from '../models/service.model';
import { MachineModel } from '../models/machine.model';

/**
 * Phase 2 Migration: Service Update Migration
 *
 * Links orphaned service updates to machines via machineId field.
 * This migration was introduced second and runs after image migration but before Firestore migration.
 *
 * Flow:
 * 1. Read service updates and machines from RTDB
 * 2. For each service update with 'machine' name but no 'machineId':
 *    - Find matching machine by name
 *    - Set machineId field
 *    - Write service update to Firestore (users/{uid}/serviceUpdates/{serviceId})
 */
export async function* migrateServiceUpdates(
  uid: string,
  db: Database,
  firestore: Firestore,
): AsyncGenerator<Progress, void, unknown> {
  // Read service updates from RTDB
  const servicesSnapshot = await get(dbRef(db, `/userProfile/${uid}/serviceupdates`));
  const servicesData = servicesSnapshot.val();

  if (!servicesData) {
    yield { type: 'done' };
    return;
  }

  // Convert object to array with id as key
  const serviceUpdates = Object.entries(servicesData).map(([id, serviceData]) =>
    ({ ...(serviceData as object), id }) as ServiceUpdateModel
  );

  const notMigratedServiceUpdates = serviceUpdates.filter(
    (su) => su.machine && !su.machineId,
  );

  // Read machines from RTDB
  const machinesSnapshot = await get(dbRef(db, `/userProfile/${uid}/machines`));
  const machinesData = machinesSnapshot.val();

  if (!machinesData) {
    yield { type: 'done' };
    return;
  }

  const machines = Object.entries(machinesData).map(([id, machineData]) =>
    new MachineModel({ ...(machineData as object), id })
  );

  if (notMigratedServiceUpdates.length === 0) {
    yield { type: 'done' };
    return;
  }

  for (const [index, serviceUpdate] of notMigratedServiceUpdates.entries()) {
    if (serviceUpdate.machine && !serviceUpdate.machineId) {
      yield {
        type: 'progress',
        current: index,
        total: notMigratedServiceUpdates.length,
      };

      try {
        const machine = machines.find((m) => m.name === serviceUpdate.machine);
        if (!machine) {
          continue;
        }
        // Update the service update with machineId
        serviceUpdate.machineId = machine.id;

        // Write directly to Firestore (not RTDB)
        const serviceData = JSON.parse(JSON.stringify(serviceUpdate));
        await setDoc(
          doc(firestore, `users/${uid}/serviceUpdates/${serviceUpdate.id}`),
          serviceData,
        );
      } catch (error) {
        captureException(error, {
          extra: {
            serviceUpdateId: serviceUpdate.id,
          },
        });
      }
    }
  }

  yield { type: 'done' };
}
